import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { getDocsIndex } from './docsSearchIndex'

const isDocsHost = () =>
  window.location.hostname === `docs.${process.env.ROOT_DOMAIN}` ||
  window.location.hostname.startsWith('docs.')

const getDocHref = (sectionId, pageId) => {
  const base = isDocsHost() ? '' : '/docs'
  return `${base}/${sectionId}/${pageId}`
}

const DocsSearch = () => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const navigate = useNavigate()

  const handleChange = (e) => {
    const value = e.target.value
    setQuery(value)

    if (!value.trim()) {
      setResults([])
      return
    }

    const index = getDocsIndex()
    const hits = index.search(value, { prefix: true, fuzzy: 0.2 })
    setResults(hits.slice(0, 8))
  }

  const handleSelect = (r) => {
    setQuery('')
    setResults([])
    navigate(getDocHref(r.sectionID, r.pageId || r.id))
  }

  const hasQuery = query.trim().length > 0

  return (
    <Box sx={{ position: 'relative', zIndex: 20 }}>
      <TextField
        fullWidth
        value={query}
        onChange={handleChange}
        placeholder="Search documentation..."
        autoComplete="off"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setQuery('')
            setResults([])
          }
          if (e.key === 'Enter' && results[0]) {
            handleSelect(results[0])
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRoundedIcon sx={{ color: '#239AA4' }} />
            </InputAdornment>
          ),
          endAdornment: hasQuery ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setQuery('')
                  setResults([])
                }}
                aria-label="Clear documentation search"
                sx={{ borderRadius: 2 }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            bgcolor: 'rgba(255,255,255,0.96)',
            boxShadow: '0 14px 38px rgba(15,23,42,0.16)',
            fontWeight: 600,
            height: 46,
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.18)',
            },
            '&:hover fieldset': {
              borderColor: '#46D7E1',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#46D7E1',
              borderWidth: 2,
            },
          },
        }}
      />

      {hasQuery && (
        <Paper
          elevation={0}
          sx={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            left: 0,
            right: 0,
            borderRadius: 2.5,
            overflow: 'hidden',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 24px 70px rgba(15,23,42,0.22)',
            zIndex: 1000,
            bgcolor: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.1,
              bgcolor: '#F8FAFC',
              borderBottom: '1px solid rgba(15,23,42,0.06)',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              {results.length ? 'Search results' : 'No matching articles'}
            </Typography>
          </Box>

          {results.length ? (
            <List disablePadding>
              {results.map((r) => (
                <ListItemButton
                  key={`${r.sectionID}-${r.id}`}
                  onClick={() => handleSelect(r)}
                  sx={{
                    py: 1.4,
                    px: 2,
                    borderTop: '1px solid rgba(15,23,42,0.04)',
                    '&:hover': {
                      bgcolor: 'rgba(35,154,164,0.06)',
                    },
                  }}
                >
                  <ListItemText
                    primary={r.title}
                    secondary={r.summary || r.section}
                    primaryTypographyProps={{
                      fontWeight: 800,
                      fontSize: 14,
                    }}
                    secondaryTypographyProps={{
                      fontSize: 12,
                      color: 'text.secondary',
                      noWrap: true,
                    }}
                  />

                  <ArrowForwardRoundedIcon
                    sx={{ color: '#239AA4', fontSize: 18 }}
                  />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Box
              sx={{
                px: 2,
                py: 2,
              }}
            >
              <Typography sx={{ color: '#64748B', fontSize: 13 }}>
                Try a module name, workflow, or feature such as shifts,
                incidents, ePCR, or vehicles.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  )
}

export default DocsSearch
